import type {
  CompleteGoodsReceiptResponse,
  CompleteSimpleGoodsReceiptRequest,
  DirectPurchaseAuthorizationResponse,
} from "@medicorepay/shared";
import { calculateLineItemTotals } from "@medicorepay/shared";
import { useEffect, useMemo, useRef, useState } from "react";

export interface ReceivingSupplier {
  id: string;
  name: string;
  phone?: string;
}

export interface ReceivingProduct {
  id: string;
  name: string;
  sku: string;
  unit: string;
  manufacturer?: string | null;
  purchasePrice: number;
  mrp: number;
  gstPercent: number;
}

export interface ReceivingPack {
  id: string;
  productId: string;
  description: string;
  unitsPerPack: number;
  purchasePrice: number;
  mrp: number;
}

export interface ReceivingPurchaseOrderLine {
  purchaseOrderItemId: string;
  product: ReceivingProduct;
  purchasePackId?: string;
  purchasePack?: ReceivingPack;
  quantity: number;
  freeQuantity: number;
  cost: number;
  mrp: number;
  gstPercent: number;
  discountPercent?: number;
  schemeDiscountPercent?: number;
}

export interface ReceivingPurchaseOrder {
  id: string;
  number: string;
  supplierId: string;
  lines: ReceivingPurchaseOrderLine[];
}

export interface SimpleReceivingViewProps {
  loadSuppliers: () => Promise<ReceivingSupplier[]>;
  searchProducts: (search: string) => Promise<ReceivingProduct[]>;
  loadProductPacks: (productId: string) => Promise<ReceivingPack[]>;
  loadPurchaseOrder?: () => Promise<ReceivingPurchaseOrder>;
  completeReceipt: (
    body: CompleteSimpleGoodsReceiptRequest,
    idempotencyKey: string,
  ) => Promise<CompleteGoodsReceiptResponse>;
  authorizeDirectPurchase?: (
    body: CompleteSimpleGoodsReceiptRequest,
    idempotencyKey: string,
  ) => Promise<DirectPurchaseAuthorizationResponse>;
  onBack: () => void;
  idempotencyScope?: string;
}

interface ReceivingLine {
  id: string;
  purchaseOrderItemId?: string;
  product: ReceivingProduct;
  packs: ReceivingPack[];
  purchasePackId: string;
  batchNumber: string;
  expiryDate: string;
  quantity: number;
  freeQuantity: number;
  cost: number;
  mrp: number;
  gstPercent: number;
  discountPercent: number;
  schemeDiscountPercent: number;
}

interface PendingCommand {
  fingerprint: string;
  idempotencyKey: string;
}

function localDate(date = new Date()) {
  const offset = date.getTimezoneOffset() * 60_000;
  return new Date(date.getTime() - offset).toISOString().slice(0, 10);
}

function nextDate(value: string) {
  const date = new Date(`${value}T00:00:00`);
  date.setDate(date.getDate() + 1);
  return localDate(date);
}

function lineId() {
  return (
    globalThis.crypto?.randomUUID?.() ??
    `${Date.now()}-${Math.random().toString(36).slice(2)}`
  );
}

function commandKey() {
  return (
    globalThis.crypto?.randomUUID?.() ??
    `receiving-${Date.now()}-${Math.random().toString(36).slice(2)}`
  );
}

function money(value: number | string) {
  return Number(value).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function requestErrorMessage(error: unknown) {
  const candidate = error as {
    response?: {
      message?: string;
      errors?: readonly unknown[];
      data?: { message?: string; errors?: readonly unknown[] };
    };
    message?: string;
  };
  const response = candidate.response?.data ?? candidate.response;
  if (response?.errors?.length) {
    return response.errors.map(String).join("; ");
  }
  return (
    response?.message || candidate.message || "Unable to complete the receipt"
  );
}

export function SimpleReceivingView({
  loadSuppliers,
  searchProducts,
  loadProductPacks,
  loadPurchaseOrder,
  completeReceipt,
  authorizeDirectPurchase,
  onBack,
  idempotencyScope = "active-store",
}: SimpleReceivingViewProps) {
  const today = useMemo(() => localDate(), []);
  const [suppliers, setSuppliers] = useState<ReceivingSupplier[]>([]);
  const [purchaseOrder, setPurchaseOrder] =
    useState<ReceivingPurchaseOrder | null>(null);
  const [supplierId, setSupplierId] = useState("");
  const [invoiceNumber, setInvoiceNumber] = useState("");
  const [invoiceDate, setInvoiceDate] = useState(today);
  const [receivedAt, setReceivedAt] = useState(today);
  const [lines, setLines] = useState<ReceivingLine[]>([]);
  const [search, setSearch] = useState("");
  const [results, setResults] = useState<ReceivingProduct[]>([]);
  const [loadingSuppliers, setLoadingSuppliers] = useState(true);
  const [searching, setSearching] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [completion, setCompletion] =
    useState<CompleteGoodsReceiptResponse | null>(null);
  const [directApproval, setDirectApproval] = useState<{
    id: string;
    fingerprint: string;
  } | null>(null);
  const requestSequence = useRef(0);
  const initializedPurchaseOrderId = useRef<string | null>(null);
  const pendingCommand = useRef<PendingCommand | null>(null);
  const pendingAuthorization = useRef<PendingCommand | null>(null);
  const storageKey = `medicorepay:receiving:${idempotencyScope}:pending`;
  const authorizationStorageKey = `${storageKey}:direct-authorization`;

  useEffect(() => {
    let active = true;
    setLoadingSuppliers(true);
    loadSuppliers()
      .then((loaded) => {
        if (active) setSuppliers(loaded);
      })
      .catch((loadError) => {
        if (active) setError(requestErrorMessage(loadError));
      })
      .finally(() => {
        if (active) setLoadingSuppliers(false);
      });
    return () => {
      active = false;
    };
  }, [loadSuppliers]);

  useEffect(() => {
    if (!loadPurchaseOrder) return;
    let active = true;
    loadPurchaseOrder()
      .then((order) => {
        if (active) setPurchaseOrder(order);
      })
      .catch((loadError) => {
        if (active) setError(requestErrorMessage(loadError));
      });
    return () => {
      active = false;
    };
  }, [loadPurchaseOrder]);

  useEffect(() => {
    if (
      !purchaseOrder ||
      initializedPurchaseOrderId.current === purchaseOrder.id
    )
      return;
    let active = true;
    Promise.all(
      purchaseOrder.lines.map(async (orderLine) => {
        const loadedPacks = await loadProductPacks(orderLine.product.id);
        const packs =
          orderLine.purchasePack &&
          !loadedPacks.some((pack) => pack.id === orderLine.purchasePack!.id)
            ? [orderLine.purchasePack, ...loadedPacks]
            : loadedPacks;
        return {
          id: lineId(),
          purchaseOrderItemId: orderLine.purchaseOrderItemId,
          product: orderLine.product,
          packs,
          purchasePackId: orderLine.purchasePackId ?? "",
          batchNumber: "",
          expiryDate: "",
          quantity: orderLine.quantity,
          freeQuantity: orderLine.freeQuantity,
          cost: orderLine.cost,
          mrp: orderLine.mrp,
          gstPercent: orderLine.gstPercent,
          discountPercent: orderLine.discountPercent ?? 0,
          schemeDiscountPercent: orderLine.schemeDiscountPercent ?? 0,
        } satisfies ReceivingLine;
      }),
    )
      .then((loadedLines) => {
        if (!active) return;
        initializedPurchaseOrderId.current = purchaseOrder.id;
        setSupplierId(purchaseOrder.supplierId);
        setLines(loadedLines);
      })
      .catch((loadError) => {
        if (active) setError(requestErrorMessage(loadError));
      });
    return () => {
      active = false;
    };
  }, [loadProductPacks, purchaseOrder]);

  useEffect(() => {
    const term = search.trim();
    if (term.length < 2) {
      setResults([]);
      setSearching(false);
      return;
    }
    const sequence = ++requestSequence.current;
    const timer = setTimeout(() => {
      setSearching(true);
      searchProducts(term)
        .then((loaded) => {
          if (sequence === requestSequence.current) setResults(loaded);
        })
        .catch((loadError) => {
          if (sequence === requestSequence.current)
            setError(requestErrorMessage(loadError));
        })
        .finally(() => {
          if (sequence === requestSequence.current) setSearching(false);
        });
    }, 250);
    return () => clearTimeout(timer);
  }, [search, searchProducts]);

  const totals = useMemo(() => {
    let subtotal = 0;
    let discount = 0;
    let tax = 0;
    for (const line of lines) {
      const calculated = calculateLineItemTotals({
        quantity: line.quantity,
        unitsPerPack: 1,
        unitPrice: line.cost,
        discountPercent: line.discountPercent,
        schemeDiscountPercent: line.schemeDiscountPercent,
        taxPercent: line.gstPercent,
      });
      subtotal += calculated.subtotal;
      discount += calculated.discountAmount + calculated.schemeDiscountAmount;
      tax += calculated.taxAmount;
    }
    return {
      subtotal: Math.round(subtotal * 100) / 100,
      discount: Math.round(discount * 100) / 100,
      tax: Math.round(tax * 100) / 100,
      total: Math.round((subtotal - discount + tax) * 100) / 100,
    };
  }, [lines]);

  const addProduct = async (product: ReceivingProduct) => {
    setError(null);
    try {
      const packs = await loadProductPacks(product.id);
      const initialPack = packs[0];
      setLines((current) => [
        ...current,
        {
          id: lineId(),
          product,
          packs,
          purchasePackId: initialPack?.id ?? "",
          batchNumber: "",
          expiryDate: "",
          quantity: 1,
          freeQuantity: 0,
          cost: initialPack?.purchasePrice || product.purchasePrice || 0,
          mrp: initialPack?.mrp || product.mrp || 0,
          gstPercent: product.gstPercent || 0,
          discountPercent: 0,
          schemeDiscountPercent: 0,
        },
      ]);
      setSearch("");
      setResults([]);
    } catch (loadError) {
      setError(requestErrorMessage(loadError));
    }
  };

  const updateLine = <K extends keyof ReceivingLine>(
    id: string,
    field: K,
    value: ReceivingLine[K],
  ) => {
    setLines((current) =>
      current.map((line) =>
        line.id === id ? { ...line, [field]: value } : line,
      ),
    );
  };

  const selectPack = (lineIdValue: string, packId: string) => {
    setLines((current) =>
      current.map((line) => {
        if (line.id !== lineIdValue) return line;
        const pack = line.packs.find((candidate) => candidate.id === packId);
        return {
          ...line,
          purchasePackId: packId,
          cost: pack?.purchasePrice || line.cost,
          mrp: pack?.mrp || line.mrp,
        };
      }),
    );
  };

  const buildRequest = (): CompleteSimpleGoodsReceiptRequest => {
    const isDirectPurchase = !purchaseOrder;
    return {
      supplierId,
      purchaseOrderId: purchaseOrder?.id,
      invoiceNumber: invoiceNumber.trim(),
      invoiceDate: `${invoiceDate}T00:00:00.000Z`,
      receivedAt: `${receivedAt}T00:00:00.000Z`,
      discountAmount: totals.discount,
      ...(isDirectPurchase
        ? {
            directPurchase: true,
            directPurchaseReason:
              "Stock received directly from supplier without a purchase order",
          }
        : {}),
      items: lines.map((line) => ({
        purchaseOrderItemId: line.purchaseOrderItemId,
        productId: line.product.id,
        purchasePackId: line.purchasePackId || undefined,
        batchNumber: line.batchNumber.trim(),
        expiryDate: `${line.expiryDate}T00:00:00.000Z`,
        quantity: line.quantity,
        freeQuantity: line.freeQuantity,
        cost: line.cost,
        mrp: line.mrp,
        gstPercent: line.gstPercent,
      })),
    };
  };

  const splitBatch = (source: ReceivingLine) => {
    setLines((current) => [
      ...current,
      {
        ...source,
        id: lineId(),
        batchNumber: "",
        expiryDate: "",
        quantity: 1,
        freeQuantity: 0,
      },
    ]);
  };

  const getPendingCommand = (fingerprint: string) => {
    if (!pendingCommand.current) {
      try {
        const stored = sessionStorage.getItem(storageKey);
        pendingCommand.current = stored
          ? (JSON.parse(stored) as PendingCommand)
          : null;
      } catch {
        pendingCommand.current = null;
      }
    }
    if (pendingCommand.current?.fingerprint === fingerprint)
      return pendingCommand.current;
    const created = { fingerprint, idempotencyKey: commandKey() };
    pendingCommand.current = created;
    try {
      sessionStorage.setItem(storageKey, JSON.stringify(created));
    } catch {
      // The in-memory key still makes retries safe for the current session.
    }
    return created;
  };

  const getPendingAuthorization = (fingerprint: string) => {
    if (!pendingAuthorization.current) {
      try {
        const stored = sessionStorage.getItem(authorizationStorageKey);
        pendingAuthorization.current = stored
          ? (JSON.parse(stored) as PendingCommand)
          : null;
      } catch {
        pendingAuthorization.current = null;
      }
    }
    if (pendingAuthorization.current?.fingerprint === fingerprint) {
      return pendingAuthorization.current;
    }
    const created = { fingerprint, idempotencyKey: commandKey() };
    pendingAuthorization.current = created;
    try {
      sessionStorage.setItem(authorizationStorageKey, JSON.stringify(created));
    } catch {
      // The in-memory key still protects retries in this session.
    }
    return created;
  };

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);
    if (!supplierId) return setError("Select a supplier");
    if (!invoiceNumber.trim())
      return setError("Enter the supplier invoice number");
    if (!invoiceDate) return setError("Enter the supplier invoice date");
    if (!receivedAt) return setError("Enter the receipt date");
    if (lines.length === 0) return setError("Add at least one product");
    for (const line of lines) {
      if (!line.batchNumber.trim())
        return setError(`Enter a batch number for ${line.product.name}`);
      if (!line.expiryDate)
        return setError(`Enter an expiry date for ${line.product.name}`);
      if (line.expiryDate <= receivedAt)
        return setError(
          `${line.product.name} must expire after the receipt date`,
        );
      if (line.quantity < 0 || line.freeQuantity < 0)
        return setError(`Enter valid quantities for ${line.product.name}`);
      if (line.quantity === 0 && line.freeQuantity === 0)
        return setError(
          `Enter a paid or promotional-free quantity for ${line.product.name}`,
        );
      if (line.cost <= 0 || line.mrp <= 0)
        return setError(`Enter cost and MRP for ${line.product.name}`);
    }

    const body = buildRequest();
    const directFingerprint = JSON.stringify(body);
    setSubmitting(true);
    try {
      let completionBody = body;
      if (!purchaseOrder && authorizeDirectPurchase) {
        if (
          directApproval?.id &&
          directApproval.fingerprint === directFingerprint
        ) {
          completionBody = {
            ...body,
            approvalRequestId: directApproval.id,
          };
        } else {
          const authorizationCommand =
            getPendingAuthorization(directFingerprint);
          const authorization = await authorizeDirectPurchase(
            body,
            authorizationCommand.idempotencyKey,
          );
          pendingAuthorization.current = null;
          try {
            sessionStorage.removeItem(authorizationStorageKey);
          } catch {
            // Authorization is already durable on the server.
          }
          if (authorization.required) {
            if (!authorization.approvalRequestId) {
              throw new Error(
                "The direct-purchase approval request was not created",
              );
            }
            setDirectApproval({
              id: authorization.approvalRequestId,
              fingerprint: directFingerprint,
            });
            setError(
              `Manager approval ${authorization.approvalRequestId} is pending. Ask an authorized manager to approve it, then select Complete receipt again.`,
            );
            return;
          }
        }
      }
      const fingerprint = JSON.stringify(completionBody);
      const pending = getPendingCommand(fingerprint);
      const result = await completeReceipt(
        completionBody,
        pending.idempotencyKey,
      );
      pendingCommand.current = null;
      try {
        sessionStorage.removeItem(storageKey);
      } catch {
        // Completion is already durable on the server.
      }
      setCompletion(result);
    } catch (submitError) {
      setError(
        `${requestErrorMessage(submitError)}. Retry uses the same safe command.`,
      );
    } finally {
      setSubmitting(false);
    }
  };

  const reset = () => {
    setSupplierId("");
    setInvoiceNumber("");
    setInvoiceDate(today);
    setReceivedAt(today);
    setLines([]);
    setCompletion(null);
    setDirectApproval(null);
    setError(null);
  };

  if (completion) {
    return (
      <div className="mx-auto max-w-3xl p-6">
        <div className="rounded-xl border border-green-200 bg-white p-8 shadow-sm">
          <div className="mb-2 text-sm font-semibold uppercase tracking-wide text-green-700">
            {completion.replayed
              ? "Already completed — safe replay"
              : "Receipt completed"}
          </div>
          <h1 className="text-3xl font-bold text-gray-900">
            {completion.grnNumber}
          </h1>
          <p className="mt-2 text-gray-600">
            Stock, supplier balance, and the receipt were posted together.
          </p>
          <dl className="mt-6 grid gap-4 rounded-lg bg-gray-50 p-5 sm:grid-cols-2">
            <div>
              <dt className="text-xs uppercase text-gray-500">Supplier</dt>
              <dd className="font-medium text-gray-900">
                {completion.supplierName}
              </dd>
            </div>
            <div>
              <dt className="text-xs uppercase text-gray-500">Invoice</dt>
              <dd className="font-medium text-gray-900">
                {completion.invoiceNumber}
              </dd>
            </div>
            <div>
              <dt className="text-xs uppercase text-gray-500">Products</dt>
              <dd className="font-medium text-gray-900">
                {completion.lineCount}
              </dd>
            </div>
            <div>
              <dt className="text-xs uppercase text-gray-500">Total</dt>
              <dd className="font-medium text-gray-900">
                ₹{money(completion.totalAmount)}
              </dd>
            </div>
          </dl>
          <div className="mt-6 flex flex-wrap gap-3">
            <button
              type="button"
              onClick={onBack}
              className="rounded-lg bg-blue-600 px-5 py-2.5 font-medium text-white hover:bg-blue-700"
            >
              View receipts
            </button>
            <button
              type="button"
              onClick={reset}
              className="rounded-lg border border-gray-300 px-5 py-2.5 font-medium text-gray-700 hover:bg-gray-50"
            >
              Receive another invoice
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="mx-auto max-w-7xl space-y-6 p-6">
      <header>
        <button
          type="button"
          onClick={onBack}
          className="mb-2 text-sm font-medium text-blue-700 hover:text-blue-900"
        >
          ← Back to goods receipts
        </button>
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Receive stock</h1>
            <p className="mt-1 text-sm text-gray-600">
              {purchaseOrder
                ? `Receiving against ${purchaseOrder.number}. Enter only the quantities and batches delivered now.`
                : "Enter the supplier invoice and received batches. A purchase order is not required."}
            </p>
          </div>
          <span className="rounded-full bg-green-100 px-3 py-1 text-xs font-semibold text-green-800">
            One-step completion
          </span>
        </div>
      </header>

      {error && (
        <div
          role="alert"
          className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800"
        >
          {error}
        </div>
      )}

      <section className="rounded-xl bg-white p-5 shadow-sm">
        <h2 className="mb-4 text-lg font-semibold text-gray-900">Invoice</h2>
        <div className="grid gap-4 md:grid-cols-4">
          <label className="text-sm font-medium text-gray-700 md:col-span-1">
            Supplier *
            <select
              value={supplierId}
              onChange={(event) => setSupplierId(event.target.value)}
              disabled={loadingSuppliers || Boolean(purchaseOrder)}
              className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 font-normal"
            >
              <option value="">
                {loadingSuppliers ? "Loading suppliers…" : "Select supplier"}
              </option>
              {suppliers.map((supplier) => (
                <option key={supplier.id} value={supplier.id}>
                  {supplier.name}
                </option>
              ))}
            </select>
          </label>
          <label className="text-sm font-medium text-gray-700">
            Invoice number *
            <input
              value={invoiceNumber}
              onChange={(event) => setInvoiceNumber(event.target.value)}
              maxLength={50}
              className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 font-normal"
              placeholder="Supplier invoice"
            />
          </label>
          <label className="text-sm font-medium text-gray-700">
            Invoice date *
            <input
              type="date"
              value={invoiceDate}
              onChange={(event) => setInvoiceDate(event.target.value)}
              className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 font-normal"
            />
          </label>
          <label className="text-sm font-medium text-gray-700">
            Received date *
            <input
              type="date"
              value={receivedAt}
              onChange={(event) => setReceivedAt(event.target.value)}
              className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 font-normal"
            />
          </label>
        </div>
      </section>

      <section className="rounded-xl bg-white p-5 shadow-sm">
        <h2 className="text-lg font-semibold text-gray-900">
          Products and batches
        </h2>
        {!purchaseOrder && (
          <div className="relative mt-4 max-w-xl">
            <input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search product by name, SKU, or barcode"
              className="w-full rounded-lg border border-gray-300 px-4 py-2.5"
            />
            {(searching || results.length > 0) && (
              <div className="absolute z-20 mt-1 max-h-72 w-full overflow-auto rounded-lg border border-gray-200 bg-white shadow-lg">
                {searching ? (
                  <div className="p-3 text-sm text-gray-500">Searching…</div>
                ) : (
                  results.map((product) => (
                    <button
                      key={product.id}
                      type="button"
                      onClick={() => void addProduct(product)}
                      className="flex w-full items-start justify-between border-b border-gray-100 px-4 py-3 text-left hover:bg-blue-50"
                    >
                      <span>
                        <span className="block font-medium text-gray-900">
                          {product.name}
                        </span>
                        <span className="text-xs text-gray-500">
                          {product.sku} · {product.manufacturer || product.unit}
                        </span>
                      </span>
                      <span className="text-sm font-medium text-blue-700">
                        Add
                      </span>
                    </button>
                  ))
                )}
              </div>
            )}
          </div>
        )}

        {lines.length === 0 ? (
          <div className="mt-6 rounded-lg border border-dashed border-gray-300 py-12 text-center text-sm text-gray-500">
            Search and add the first product from this invoice.
          </div>
        ) : (
          <div className="mt-6 overflow-x-auto">
            <table className="min-w-[1100px] w-full border-collapse text-sm">
              <thead className="bg-gray-50 text-left text-xs uppercase text-gray-500">
                <tr>
                  <th className="px-3 py-3">Product</th>
                  <th className="px-3 py-3">Pack</th>
                  <th className="px-3 py-3">Batch</th>
                  <th className="px-3 py-3">Expiry</th>
                  <th className="px-3 py-3">Qty</th>
                  <th className="px-3 py-3">Free</th>
                  <th className="px-3 py-3">Cost</th>
                  <th className="px-3 py-3">MRP</th>
                  <th className="px-3 py-3">GST %</th>
                  <th className="px-3 py-3 text-right">Line total</th>
                  <th className="px-3 py-3" />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {lines.map((line) => {
                  const lineTotal = calculateLineItemTotals({
                    quantity: line.quantity,
                    unitsPerPack: 1,
                    unitPrice: line.cost,
                    discountPercent: line.discountPercent,
                    schemeDiscountPercent: line.schemeDiscountPercent,
                    taxPercent: line.gstPercent,
                  }).totalAmount;
                  return (
                    <tr key={line.id}>
                      <td className="px-3 py-3">
                        <div className="font-medium text-gray-900">
                          {line.product.name}
                        </div>
                        <div className="text-xs text-gray-500">
                          {line.product.sku}
                        </div>
                      </td>
                      <td className="px-3 py-3">
                        {line.packs.length ? (
                          <select
                            aria-label={`${line.product.name} pack`}
                            value={line.purchasePackId}
                            disabled={Boolean(line.purchaseOrderItemId)}
                            onChange={(event) =>
                              selectPack(line.id, event.target.value)
                            }
                            className="w-32 rounded border border-gray-300 px-2 py-1.5"
                          >
                            {line.packs.map((pack) => (
                              <option key={pack.id} value={pack.id}>
                                {pack.description} ×{pack.unitsPerPack}
                              </option>
                            ))}
                          </select>
                        ) : (
                          <span className="text-gray-600">Base unit</span>
                        )}
                      </td>
                      <td className="px-3 py-3">
                        <input
                          aria-label={`${line.product.name} batch`}
                          value={line.batchNumber}
                          onChange={(event) =>
                            updateLine(
                              line.id,
                              "batchNumber",
                              event.target.value,
                            )
                          }
                          className="w-28 rounded border border-gray-300 px-2 py-1.5"
                          placeholder="Batch"
                        />
                      </td>
                      <td className="px-3 py-3">
                        <input
                          aria-label={`${line.product.name} expiry`}
                          type="date"
                          min={nextDate(receivedAt)}
                          value={line.expiryDate}
                          onChange={(event) =>
                            updateLine(
                              line.id,
                              "expiryDate",
                              event.target.value,
                            )
                          }
                          className="w-36 rounded border border-gray-300 px-2 py-1.5"
                        />
                      </td>
                      {(
                        [
                          "quantity",
                          "freeQuantity",
                          "cost",
                          "mrp",
                          "gstPercent",
                        ] as const
                      ).map((field) => (
                        <td key={field} className="px-3 py-3">
                          <input
                            aria-label={`${line.product.name} ${field}`}
                            type="number"
                            min={0}
                            max={field === "gstPercent" ? 100 : undefined}
                            step={
                              field === "quantity" || field === "freeQuantity"
                                ? 1
                                : 0.01
                            }
                            value={line[field]}
                            disabled={
                              Boolean(line.purchaseOrderItemId) &&
                              ["cost", "mrp", "gstPercent"].includes(field)
                            }
                            onChange={(event) =>
                              updateLine(
                                line.id,
                                field,
                                Number(event.target.value),
                              )
                            }
                            className="w-20 rounded border border-gray-300 px-2 py-1.5 text-right"
                          />
                        </td>
                      ))}
                      <td className="px-3 py-3 text-right font-semibold text-gray-900">
                        ₹{money(lineTotal)}
                      </td>
                      <td className="px-3 py-3 text-right">
                        {line.purchaseOrderItemId && (
                          <button
                            type="button"
                            onClick={() => splitBatch(line)}
                            className="mr-3 text-blue-700 hover:text-blue-900"
                            aria-label={`Split ${line.product.name} into another batch`}
                          >
                            Split batch
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={() =>
                            setLines((current) =>
                              current.filter(
                                (candidate) => candidate.id !== line.id,
                              ),
                            )
                          }
                          className="text-red-600 hover:text-red-800"
                          aria-label={`Remove ${line.product.name}`}
                        >
                          Remove
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section className="flex flex-col items-end gap-4 rounded-xl bg-white p-5 shadow-sm sm:flex-row sm:justify-between">
        <p className="max-w-xl text-sm text-gray-600">
          Completion posts the receipt, supplier balance, legacy batch,
          inventory lot, ledger movement, and outbox event in one retry-safe
          transaction.
        </p>
        <div className="min-w-72">
          <div className="flex justify-between text-sm text-gray-600">
            <span>Subtotal</span>
            <span>₹{money(totals.subtotal)}</span>
          </div>
          <div className="mt-1 flex justify-between text-sm text-gray-600">
            <span>Discount</span>
            <span>-₹{money(totals.discount)}</span>
          </div>
          <div className="mt-1 flex justify-between text-sm text-gray-600">
            <span>GST</span>
            <span>₹{money(totals.tax)}</span>
          </div>
          <div className="mt-2 flex justify-between border-t pt-2 text-lg font-bold text-gray-900">
            <span>Total</span>
            <span>₹{money(totals.total)}</span>
          </div>
          <button
            type="submit"
            disabled={submitting || lines.length === 0}
            className="mt-4 w-full rounded-lg bg-green-600 px-5 py-3 font-semibold text-white hover:bg-green-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {submitting ? "Completing safely…" : "Complete receipt"}
          </button>
        </div>
      </section>
    </form>
  );
}
